// Stage 3B:Blog list API
// Stage 3C:add createDraft(POST /api/blog/drafts)

import { apiFetch } from "./client";

export interface SyncMeta {
  stale: boolean;
  sync_status: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  ttl_seconds: number;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  status: "publish" | "draft" | "future" | "private" | "pending" | string;
  link: string | null;
  date_gmt: string | null;
  modified_gmt: string | null;
  author: number | null;
  excerpt: string;
}

export interface BlogListResponse {
  items: BlogPost[];
  total: number;
  page: number;
  per_page: number;
  sync_meta?: SyncMeta;
}

export interface BlogPostActionResponse {
  status: "publish" | "draft" | "future" | "private" | "pending" | string;
  wordpress_post_id: number;
  link: string | null;
  title: string;
}

/**
 * Stage Blog Bulk(2026-09-16):一括 publish / unpublish / delete のレスポンス。
 * - 1 件ごとの成否を results に格納(全体エラーではなく partial failure を許容)
 */
export interface BlogBulkActionResponse {
  action: "publish" | "unpublish" | "delete";
  succeeded: number;
  failed: number;
  results: Array<{
    post_id: number;
    ok: boolean;
    status?: "publish" | "draft" | "trash" | string;
    error_code?: string;
    error_message?: string;
  }>;
}

export interface CreateDraftRequest {
  title: string;
  content: string;
}

export const blogApi = {
  listPosts: (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    /** WP から除外する post status(例:["trash"]) */
    exclude?: string[];
  }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.per_page) qs.set("per_page", String(params.per_page));
    if (params?.status) qs.set("status", params.status);
    if (params?.exclude && params.exclude.length > 0) {
      // exclude[]=trash は複数値のため、URLSearchParams.append で重複 key を作る
      for (const v of params.exclude) qs.append("exclude[]", v);
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<BlogListResponse>(`/blog/posts${suffix}`);
  },

  refreshPosts: (force = true) =>
    apiFetch<{ refreshed: string[]; skipped: string[] }>("/blog/posts/refresh", {
      method: "POST",
      body: JSON.stringify({ force }),
    }),

  // Stage 3C:下書き作成。status は backend 强制 "draft"(無視前端)。
  createDraft: (body: CreateDraftRequest) =>
    apiFetch<BlogPost>("/blog/drafts", {
      method: "POST",
      body: JSON.stringify({
        title: body.title,
        content: body.content,
      }),
    }),

  // Stage 4B UX 拡張(2026-07-06):複製 / 公開 / 下書きに戻す / 削除
  duplicatePost: (postId: number) =>
    apiFetch<BlogPostActionResponse>(`/blog/posts/${postId}/duplicate`, {
      method: "POST",
    }),

  publishPost: (postId: number) =>
    apiFetch<BlogPostActionResponse>(`/blog/posts/${postId}/publish`, {
      method: "POST",
    }),

  unpublishPost: (postId: number) =>
    apiFetch<BlogPostActionResponse>(`/blog/posts/${postId}/unpublish`, {
      method: "POST",
    }),

  deletePost: (postId: number) =>
    apiFetch<BlogPostActionResponse>(`/blog/posts/${postId}`, {
      method: "DELETE",
    }),

  /**
   * 一括 publish / unpublish / delete(2026-09-16)。
   * - publish / unpublish:system_admin + editor が呼べる
   * - delete:system_admin のみ(backend が 403 を返す)
   */
  bulkAction: (
    action: "publish" | "unpublish" | "delete",
    postIds: number[],
  ) =>
    apiFetch<BlogBulkActionResponse>(`/blog/posts/bulk`, {
      method: "POST",
      body: JSON.stringify({ action, post_ids: postIds }),
    }),

  // Stage 5 准备(2026-07-06):編集 modal 用 GET 詳細 + PATCH 更新
  // - GET /api/blog/posts/{id}    : 単一記事詳細(編集 modal 起動)
  // - PATCH /api/blog/posts/{id}  : title + content のみ更新(status 禁止)
  getPostDetail: (postId: number) =>
    apiFetch<BlogPostDetail>(`/blog/posts/${postId}`),

  updatePost: (postId: number, body: UpdatePostRequest) =>
    apiFetch<BlogPostDetail>(`/blog/posts/${postId}`, {
      method: "PATCH",
      body: JSON.stringify({
        title: body.title,
        content: body.content,
      }),
    }),
};

// 2026-07-06:編集 modal 用
// GET /api/blog/posts/{id} のレスポンス data
export interface BlogPostDetail {
  id: number;
  title: string;
  content: string;        // WP rendered HTML
  status: "publish" | "draft" | "future" | "private" | "pending" | string;
  link: string | null;
  created_at: string;     // ISO8601(UTC)
  updated_at: string;     // ISO8601(UTC)
}

// PATCH /api/blog/posts/{id} の body(title + content のみ)
export interface UpdatePostRequest {
  title: string;
  content: string;
}
